import {
  GeometrySnapshot,
  RenderPlan,
  RenderCursors,
  RowRenderMode,
} from "@tento-lona/sheets/platform";
import type {
  RenderCursor,
  RenderSkip,
  RenderSurfaceLease,
} from "@tento-lona/sheets/platform";
import { type Option, RenderFlags, type RowLocalId } from "@tento-lona/sheets";
import { type SheetRow, SheetViewModel } from "@tento-lona";
import { platformOf } from "@tento-lona/host";
import { DateTime, NaiveDate, Utc } from "@tento-chrono";
import { type RowResidency, RowResidencyOwner } from "./row-residency.ts";
import { placeCell } from "./place-cell.ts";
import { LiveData } from "@tento-core/livedata";
import { Tracing } from "@tento-core/tracing";
import { CellGroup } from "@tento-ui/components/sheet/cell-group";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { rowIdentity } from "@tento-ui/components/sheet/row-identity";
import { SheetCell } from "@tento-ui/components/sheet/sheet-cell";
import { nonnull } from "@tento-core/assert";
import { SheetColumn } from "../sheet-column.ts";
import { SheetDelegate } from "../sheet-delegate.ts";
import { SheetBinder } from "../sheet-index-renderer.ts";
import { StaticChildManager } from "../static-child-manager.ts";
import { DomReconciler } from "./dom-reconciler.ts";
import {
  CELL_BINDING_PLUGIN_ATTR,
  CELL_BINDING_PLUGIN_ROLE_ATTR,
  CellContentBuilder,
  DefaultBinderHelpers,
  SubcolumnCache,
} from "./default-binder-helpers.ts";
import type { CellBinding } from "./default-binder-helpers.ts";
import { LiveSceneRunner } from "./live-scene-runner.ts";
import { FrameMarks } from "@tento-ui/frame-marks";
import { AuditRate, RefereeLedger } from "./referee-ledger.ts";
import {
  type ApplyContext,
  ColumnBindPasses,
  type ColumnBindPort,
  type ColumnPassInput,
} from "./column-bind-passes.ts";
import {
  cellStructureHonorable,
  cellDisplaysColumn as $cellDisplaysColumn,
  cellDisplaysRowAndColumn as $cellDisplaysRowAndColumn,
  type CellFingerprint as $CellFingerprint,
  type CompletedBind as $CompletedBind,
  fingerprint as $fingerprint,
  fingerprintChanged as $fingerprintChanged,
  type PendingCellSample as $PendingCellSample,
} from "./cell-identity.ts";

/**
 * Slices 2 + 3 — the structure source and the per-cell honor — are no longer
 * separate consts here. Both, plus the LR6 render skip in the `sheets` package,
 * read the one SDK 2.0 switch, the sheet's `registries.render.live()`
 * (`RenderFlags`, `sheets/src/render/render-flags.ts`, whose shipped default is where
 * the pipeline is promoted or demoted in one line).
 *
 * The default-column live commit routes its STRUCTURE through the
 * SceneDiff-driven `compileSceneCommit` bundle instead of the positional
 * `compileCommitBundle`. BOTH bundles are compiled every full rebuild and
 * refereed (symmetric); the flag only picks which one feeds `applyStructure` /
 * `applyCells`. (The default-column commit always runs through the compiled
 * `CommitBundle` + `DomReconciler` applier; the fused inline kill switch that
 * `COMMIT_BUNDLE_LIVE=false` once selected is retired.)
 *
 * The per-cell pass HONORS the `classifyCellDirectives` output in `applyCells` —
 * skipping unchanged survivors, reflowing layout/span-only survivors — instead
 * of rebinding every cell. While dark the directives are still CLASSIFIED +
 * REFEREED every full rebuild (the cell-scene discipline), but
 * `applyCells(…, false)` rebinds every cell, byte-identical to slices 1–2.
 */

/**
 * Pure-audit sampling rate for the dark scene pipeline (build + diff + both
 * compilers + both referees) — the fraction of full-rebuild binds that pay for
 * it while NOTHING consumes its output.
 */
const DARK_SCENE_SAMPLE_RATE = 0.05;

/**
 * The effective `LiveSceneRunner` sample rate for this bind, resolved fresh at
 * every build. THE INVARIANT: the moment the scene bundle or the classified
 * directives feed the live path — the single SDK 2.0 switch, however it was
 * resolved — the rate is 1.0, because a sampled-out bind would starve a live
 * consumer (and would make a measurement session dishonest). Sampling applies
 * ONLY to the pure-audit configuration, where the scene's only readers are the
 * two referees and their breadcrumbs.
 */
function darkSceneSampleRate(flags: RenderFlags): AuditRate {
  return flags.live() || flags.diagnostics()
    ? AuditRate.ALWAYS
    : AuditRate.parse(DARK_SCENE_SAMPLE_RATE);
}

export class DefaultBinder implements SheetBinder {
  #captureApplyContext(): ApplyContext {
    return { epoch: this.#contextEpoch, cursor: this.#renderCursor, generation: this.handle.bindGeneration() };
  }

  #applyCurrent(context: ApplyContext): boolean {
    return context.epoch === this.#contextEpoch;
  }

  /**
   * One guarded apply step (F13): run `fn` only while `context` is still the
   * binder's current apply context; `false` means the pass was superseded
   * (a reset or detach) and the caller must stop.
   */
  #step(context: ApplyContext, fn: () => void): boolean {
    if (!this.#applyCurrent(context)) return false;
    fn();
    return true;
  }

  #contextEpoch = 0;
  /**
   * N3-3: the last full rebind each cell COMPLETED — context epoch, row and
   * time state. A membership-only pass keeps a cell only against this stamp,
   * so a bind that stopped early (epoch check, throw), a context reset, or a
   * past/current flip while scrolling never passes as "already bound".
   */
  readonly #completedBinds = new WeakMap<SheetCell, DefaultBinder.CompletedBind>();
  /** Resident cells a membership-only pass kept without rebinding (F10; counting). */
  #membershipKeeps = 0;
  /** How many cells membership-only passes have kept (F10's counting seam). */
  get membershipKeeps(): number {
    return this.#membershipKeeps;
  }
  /** The sheet this binder last read residency for (a different one clears its row modes). */
  #residencySheet: Option<SheetViewModel.Surface<SheetBinder>> = null;

  /** The sheet's residency, from the owner this binder shares with its sheet element. */
  #residencyOf(sheet: SheetViewModel.Surface<SheetBinder>): RowResidency {
    if (this.#residencySheet != null && this.#residencySheet !== sheet) this.clearRowModes();
    this.#residencySheet = sheet;
    return this.residency.of(sheet);
  }

  /** Retain scenes only for columns the host still renders. */
  retainColumns(columns: Iterable<ColumnIndex.Key.Resolved.Default>): void {
    this.#liveScene.retainColumns(columns);
  }

  retireColumn(column: ColumnIndex.Key.Resolved.Default): void {
    this.#liveScene.retireColumn(column);
  }

  #subcolumnCache = new SubcolumnCache();
  #cellContentBuilder = new CellContentBuilder();
  #previousRenderState = new Map<RowLocalId, RenderPlan.PreviousState>();

  /**
   * Manager for static child rendering.
   * Handles rows that span the entire visible viewport.
   */
  #staticChildManager: Option<StaticChildManager> = null;

  /**
   * Tracks the current render mode for each row.
   * Used to detect mode changes and trigger appropriate cleanup.
   */
  #rowModes = new Map<RowLocalId, RowRenderMode>();

  /**
   * Per-binder paint surface in the current sheet's render ledger. Separate
   * surface ids prevent two binders from accepting each other's recorded paint.
   * The cursor is absent for standalone sheets without a ledger, and is reminted
   * when the registry changes. Row records retire with the binder's row modes.
   * When the sheet's `registries.render.live()` is on, cursor decisions participate in skip
   * eligibility. With live rendering disabled, decisions are audited while
   * content binding proceeds normally.
   */
  #renderCursor: Option<RenderCursor> = null;

  /**
   * The registry `renderCursor` was minted from — the re-mint guard. A binder
   * (and its host `std-sheet` element) OUTLIVES the sheet it paints: every app
   * boot passes through a loading/last-visited sheet before settling, and every
   * in-session sheet switch swaps `rowsSheet` under the same binder. A cursor
   * kept across that swap probes the OLD sheet's store — whose `LedgerBinding`
   * lookup misses every new-sheet row and whose ledger no new-sheet write ever
   * bumps — so every version compare reads frozen `0 === 0` and, live, every
   * row of the new sheet is permanently skip-eligible. That stale-cursor
   * compare is what ate the created event's paint (2026-08-07 demote; measured
   * live 2026-08-10: create's bind pass with the stale cursor = 16 authorized
   * skips + no chip; identical create with a cursor on the current registry =
   * chip paints). `ensureRenderSkip` re-mints whenever this identity changes.
   */
  #renderCursorRegistry: Option<RenderCursors> = null;

  /**
   * The lease `renderCursor` is borrowed under. The registry keeps a
   * surface alive only while something holds it, so the binder releases the old
   * lease before taking one on a new registry — otherwise a cached sheet's
   * registry accumulates the cursor and row records of every binder that ever
   * painted it, and nothing can ever prune them (rows are reconciled, surfaces
   * were not).
   */
  #renderCursorLease: Option<RenderSurfaceLease> = null;

  /** Stable per-binder-instance surface id for `renderCursor` (see above). */
  readonly #cursorSurfaceId = RenderCursors.uniqueSurfaceId("dom-default");

  /**
   * Durable verdict counts for BOTH referee layers + this instance's loud
   * circuit breaker (`referee-ledger.ts`). The `LiveSceneRunner` pull-based
   * tallies stay exactly as they are; this is the readable aggregation layer
   * above them — and the only place a loud verdict acquires a consequence.
   * Declared BEFORE `liveScene`: field initializers run in declaration order and
   * the runner records its sampling outcomes here.
   */
  readonly #referees = RefereeLedger.forBinder();

  /**
   * Builds and diffs default-column scenes from the current rows, plans and
   * bindings. The runner records scene state; the binder applies DOM effects.
   * Live rendering uses the scene bundle and classified cell directives at
   * full sampling. Audit-only rendering samples unless diagnostics require
   * every bind. Sheets without a rows-side ledger produce no scene.
   */
  readonly #liveScene = new LiveSceneRunner(
    this.#referees,
    () => darkSceneSampleRate(this.#renderFlags),
    AuditRate.ALWAYS,
  );

  /**
   * The render-pipeline flags of the sheet being bound
   * (`sheet.handle.model.registries.render`, the client's
   * `SheetRegistries.render`), resolved at the top of every bind by
   * `ensureRenderSkip`. Each decision site reads it fresh, so a flipped
   * `RenderFlagControls` takes effect on the next bind.
   */
  #renderFlags: RenderFlags = RenderFlags.SHIPPED;

  /**
   * Per-bind memo of the LR6 skip decision per row. `decide` re-reads the row's
   * ledger versions on every call, and one bind can consult the SAME row from
   * both the directive pre-pass and the full-rebind gate. (The skip decision's
   * own ledger term goes through `RenderCursor.shouldRebind` — the only place
   * the structural-epoch gate lives — and is memoized per pass by
   * `cellEligibility`, not here.) Nothing moves a row's recorded versions
   * between those sites within a pass — `markPainted` / `evict` run at the END of
   * that row's own bind — so one probe per row per bind is exact. Cleared at the
   * top of every bind.
   */
  readonly #decisionsThisBind = new Map<RowLocalId, RenderSkip.Decision>();

  /**
   * Decision-free applier for the compiled `CommitBundle` (the un-fused commit
   * path). Owns no state; the per-column effects live in the
   * `CommitHost` `onBind` builds each pass.
   */
  readonly #reconciler = new DomReconciler();

  /**
   * Get or create the static child manager (lazy initialization).
   */
  #getStaticChildManager(): StaticChildManager {
    if (!this.#staticChildManager) {
      this.#staticChildManager = new StaticChildManager(
        this.handle,
        this.onFocusCell,
        this.onBlurCell,
      );
    }
    return this.#staticChildManager;
  }

  /** What the column passes (`column-bind-passes.ts`) reach of this binder; built on first use. */
  #portMemo: Option<ColumnBindPort> = null;
  get #port(): ColumnBindPort {
    return this.#portMemo ??= {
      handle: this.handle,
      liveScene: this.#liveScene,
      referees: this.#referees,
      reconciler: this.#reconciler,
      previousRenderState: this.#previousRenderState,
      subcolumnCache: this.#subcolumnCache,
      renderFlags: () => this.#renderFlags,
      renderCursor: () => this.#renderCursor,
      contextEpoch: () => this.#contextEpoch,
      applyCurrent: (context) => this.#applyCurrent(context),
      createPlanContext: (sheet, column, widthPx, now) => this.#createPlanContext(sheet, column, widthPx, now),
      mountCell: () => {
        const $cell = SheetCell.make() as SheetCell;
        $cell.slot = "cells";
        $cell.onFocusCell = this.onFocusCell;
        $cell.onBlurCell = this.onBlurCell;
        return $cell;
      },
      notifyCellRecycled: (oldContentType, $cell) => this.#notifyCellRecycled(oldContentType, $cell),
      applyPlanResize: (...args) => this.#applyPlanResize(...args),
      applyPlanFullRebind: (...args) => this.#applyPlanFullRebind(...args),
      applyPlanReflow: (...args) => this.#applyPlanReflow(...args),
      completedBind: ($cell) => this.#completedBinds.get($cell) ?? null,
      noteMembershipKeep: () => {
        this.#membershipKeeps++;
      },
      fingerprint: ($cell) => DefaultBinder.fingerprint($cell),
      decideOnce: (rc, rowId) => this.#decideOnce(rc, rowId),
      updatePreviousState: (plans) => this.#updatePreviousState(plans),
    };
  }

  constructor(
    readonly handle: SheetDelegate,
    readonly onFocusCell: Option<(cell: SheetCell.CellId) => void>,
    readonly onBlurCell: Option<(cell: SheetCell.CellId) => void>,
    readonly currentTime: LiveData<DateTime<Utc>>,
    /** The residency owner shared with the sheet element (one build per sheet geometry). */
    readonly residency: RowResidencyOwner,
  ) {}

  /**
   * Update static children for spans that exceed the visible viewport.
   * Called when the viewport columns change.
   */
  updateStaticChildSpans(
    sheet: SheetViewModel.Surface<SheetBinder>,
    visibleColumns: ColumnIndex.Key.Resolved.Default.Range,
  ): void {
    const manager = this.#getStaticChildManager();
    if (manager.size === 0) return;

    const now = this.currentTime.get();
    manager.update(sheet, visibleColumns, now);
  }

  /**
   * Clean up static children for spans that are no longer needed.
   */
  cleanupStaticChildSpans(): void {
    if (this.#staticChildManager) {
      this.#staticChildManager.clear();
    }
  }

  /**
   * Clear all row modes. Called when timescale changes to force
   * mode recomputation and cleanup on next bind.
   */
  clearRowModes(): void {
    this.#contextEpoch++;
    this.#liveScene.reset();
    this.#residencySheet = null;
    this.residency.invalidate();
    // Clean up any static spans before clearing modes
    this.cleanupStaticChildSpans();
    this.#rowModes.clear();
    this.#previousRenderState.clear();
    // LR6: a bulk mode/state reset forces a full rebind of every row, so the
    // rendered-version records are stale — drop them (mirror the two maps above).
    this.#renderCursor?.reset();
  }

  /**
   * Re-arm this binder's loud circuit breaker: the production flag path may
   * select the scene bundle again, and the next loud verdict announces again.
   * The recorded evidence survives. Deliberately explicit — the breaker never
   * self-heals, so a divergent scene program cannot quietly resume shipping.
   */
  resetSceneBreaker(): void {
    this.#referees.resetBreaker();
  }

  /** This binder's referee counts (both layers) + breaker state — for dev tooling. */
  refereeCounters(): RefereeLedger.Counters {
    return this.#referees.snapshot();
  }

  /**
   * Bind this binder's render cursor (row-identity ledger, L5 promotion) to the
   * CURRENT sheet's registry. The registry's probe
   * (`SourceRowCellData.currentVersions`) owns the output-node key resolution, so
   * the compare node matches the cell-write bump exactly — but ONLY while the
   * cursor and the sheet share a registry. The binder outlives its sheet (boot
   * passes through a loading/last-visited sheet; switches swap `rowsSheet` in
   * place), so this re-minted whenever the registry identity changes — a kept
   * stale cursor compares every row against the OLD sheet's ledger, reads
   * frozen versions, and live-skips every paint (see `renderCursorRegistry`).
   * Same registry ⇒ keep the lease, the cursor and its records. New registry ⇒
   * **release the old lease first** (the surface this binder is walking
   * away from must not stay registered on a sheet that may live on in the cache)
   * and acquire one on the new registry under this binder's surface id. No
   * rows-side ledger (legacy / standalone) → `null`, every LR6 call a `?.`
   * no-op (degrade-gracefully — the L2/L3/L4 pattern).
   *
   * A visit therefore always starts with empty records and every row's first
   * bind paints — including a RETURN to a still-live registry, which used to
   * reattach this binder's own records through `cursor()`'s re-entry semantics.
   * Releasing is what makes that reattachment impossible, and the conservative
   * direction is the safe one: a dropped record costs one repaint, whereas a
   * record kept across an absence the binder cannot audit (slots recycled to
   * other rows in the meantime) is a wrongly-authorized skip.
   *
   * `mode: "follow-flags"` = follow the single SDK 2.0 switch, read fresh at each
   * decision, so a mid-session spotlight toggle reaches an already-minted
   * cursor.
   */
  #ensureRenderSkip(sheet: SheetViewModel.Surface<SheetBinder>): void {
    this.#renderFlags = sheet.handle.model.registries.render;
    this.attachRenderSurface(platformOf(sheet.handle).renderCursors());
  }

  /**
   * The acquire half of the lease pair, over the registry alone — the whole of
   * the state machine `ensureRenderSkip` describes, with the DOM-coupled lookup
   * (`platformOf(sheet.handle)…`) left behind in that one-line adapter. Split so
   * the lifetime contract can be driven headlessly, since the view model the
   * bind path carries cannot be built without a browser runtime.
   *
   * `null` detaches (legacy / standalone sheets with no rows-side ledger), which
   * is the same release `dispose` performs — a binder that lands on a
   * ledger-less sheet has genuinely stopped painting the one it came from.
   */
  attachRenderSurface(registry: Option<RenderCursors>): void {
    if (registry === this.#renderCursorRegistry) return;
    this.#contextEpoch++;
    this.#liveScene.reset();
    this.#residencySheet = null;
    this.residency.invalidate();
    this.#previousRenderState.clear();
    this.#rowModes.clear();
    this.cleanupStaticChildSpans();
    this.#renderCursorLease?.release();
    const lease = registry != null
      ? registry.acquire(this.#cursorSurfaceId, { mode: "follow-flags" })
      : null;
    this.#renderCursorRegistry = registry;
    this.#renderCursorLease = lease;
    this.#renderCursor = lease?.cursor ?? null;
  }

  /**
   * The other half of `attachRenderSurface`: hand the surface back
   * when this binder stops painting altogether. The acquire half only releases
   * on a SWITCH — it is driven by a bind, and a binder being torn down
   * never binds again — so without this the last registry a binder visited keeps
   * its cursor and every row record on it forever. That leak is invisible to
   * `witness/render-cursor-leak`, which can only fire on a re-acquire; the
   * `render-cursor/lease` census is the only place it shows, as a `surfaces`
   * count that never comes back to baseline.
   *
   * Clearing the registry identity alongside the lease is what makes this safe
   * to call early: the guard in `attachRenderSurface` is `registry ===
   * this.renderCursorRegistry`, so leaving it set would make the next bind
   * early-return onto a cursor that has been reset and unregistered. Nulled, a
   * re-attached element's first bind re-acquires under the same `cursorSurfaceId`
   * — a fresh mint with `leases === 0`, so it trips no witness — and repaints
   * every row, which is the conservative direction the acquire half already
   * documents.
   *
   * Idempotent twice over: the field is nulled here, and `release()` is itself
   * per-lease idempotent, so a second disconnect cannot decrement a refcount a
   * co-holder still owns.
   */
  dispose(): void {
    this.clearRowModes();
    this.#liveScene.reset();
    this.#residencySheet = null;
    this.residency.invalidate();
    this.#renderCursorLease?.release();
    this.#renderCursorLease = null;
    this.#renderCursor = null;
    this.#renderCursorRegistry = null;
  }

  /**
   * `rc.decide(rowId)` deduplicated across this bind's probe sites (see
   * `decisionsThisBind`). Bypassed in a measurement session so it sees the
   * unchanged raw probe + counter stream.
   */
  #decideOnce(rc: RenderCursor, rowId: RowLocalId): RenderSkip.Decision {
    if (this.#renderFlags.diagnostics()) return rc.decide(rowId);
    const memo = this.#decisionsThisBind.get(rowId);
    if (memo != null) return memo;
    const decision = rc.decide(rowId);
    this.#decisionsThisBind.set(rowId, decision);
    return decision;
  }

  /**
   * LR6 finish (row-identity ledger, L5) — run at every `applyPlanFullRebind`
   * exit. Referees a would-skip decision against ground truth (did the cell's
   * output fingerprint move while binding anyway?), then records the row's
   * rendered versions on a full bind or evicts them on a partial/hidden exit
   * (never skip a partially-rendered cell next time).
   */
  #finishRenderSkip(
    decision: Option<RenderSkip.Decision>,
    pre: Option<DefaultBinder.CellFingerprint>,
    $cell: SheetCell,
    rowId: RowLocalId,
    fullBind: boolean,
    applyContext: ApplyContext,
  ): void {
    if (!this.#applyCurrent(applyContext)) return;
    const rc = applyContext.cursor;
    if (rc == null) return;
    if (decision != null && decision.wouldSkip && pre != null) {
      rc.refereeAfterBind(
        decision,
        DefaultBinder.fingerprintChanged(pre, DefaultBinder.fingerprint($cell)),
      );
    }
    if (fullBind) rc.markPainted(rowId);
    else rc.evict(rowId);
  }

  /**
   * Update the render mode for a row, triggering cleanup if the mode changed.
   * Returns whether the cell should skip normal rendering.
   */
  #updateRowMode(
    rowId: RowLocalId,
    newMode: RowRenderMode,
    $cell: SheetCell,
  ): void {
    const prevMode = this.#rowModes.get(rowId);

    // If mode hasn't changed, nothing to do
    if (prevMode && RowRenderMode.equals(prevMode, newMode)) {
      return;
    }

    // Clean up previous mode if it exists and differs
    if (prevMode) {
      this.#cleanupRowMode(rowId, prevMode, $cell);
    }

    // Store new mode
    this.#rowModes.set(rowId, newMode);
  }

  /**
   * Perform cleanup specific to a row's previous render mode.
   */
  #cleanupRowMode(
    rowId: RowLocalId,
    prevMode: RowRenderMode,
    $cell: SheetCell,
  ): void {
    switch (prevMode.type) {
      case "spanned":
        $cell.resetSpanningStyles();
        if (prevMode.isStatic) {
          this.#getStaticChildManager().unregister(rowId);
        }
        break;

      case "span-stacked":
        $cell.resetSpanningStyles();
        this.#getStaticChildManager().unregister(rowId);
        break;

      case "stacked":
        // Stacked cells are rebuilt by bindContent, no special cleanup needed
        break;

      case "normal":
        // Normal cells need no special cleanup
        break;
    }
  }

  /**
   * The SDK reading of the sheet's current tento-core layout
   * (`GeometrySnapshot.layoutSpecOf`), kept for as long as the layout is the
   * same object: a render plan reads it once per column, the layout changes
   * far less often.
   */
  #planLayout: Option<{
    readonly core: SheetViewModel.Surface<SheetBinder>["layout"];
    readonly read: RenderPlan.LayoutProvider["layout"];
  }> = null;

  #planLayoutOf(core: SheetViewModel.Surface<SheetBinder>["layout"]): RenderPlan.LayoutProvider["layout"] {
    if (this.#planLayout?.core === core) return this.#planLayout.read;
    const read = GeometrySnapshot.layoutSpecOf(core);
    this.#planLayout = { core, read };
    return read;
  }

  /**
   * Create render plan context from current state.
   */
  #createPlanContext(
    sheet: SheetViewModel.Surface<SheetBinder>,
    column: ColumnIndex.Key.Resolved.Default,
    widthPx: number,
    now: DateTime<Utc>,
  ): RenderPlan.Context<ColumnIndex.Key.Resolved.Default> {
    const mapper = sheet.indexRenderer.mapper;
    return {
      // The plan reads the layout in the SDK's types (`null`, never
      // `undefined`), read once per layout off tento-core's.
      rowIndexOf: (id) => nonnull(this.#residencyOf(sheet).semanticIndices.get(id), "render plan semantic index"),
      sheet: { timescale: sheet.timescale, layout: this.#planLayoutOf(sheet.layout) },
      column,
      widthPx,
      now,
      visibleWidthPx: this.handle.state.visibleWidthPx ?? 0,
      resolvedWidthPx: this.handle.state.resolvedWidthPx,
      ports: {
        isDuplicateDate: (date: NaiveDate) =>
          mapper.resolveKeyToDefault(
            ColumnIndex.Key.fromDate(date, "lower"),
            "lower",
          ).offset !==
          mapper.resolveKeyToDefault(
            ColumnIndex.Key.fromDate(date, "upper"),
            "upper",
          ).offset,
      },
      getColumnRangeWidthPx: (start, end) =>
        this.handle.state.getColumnRangeWidthPx(start, end),
    };
  }

  /**
   * Notify a plugin that all cell instances for this SheetCell are being recycled.
   * Handles both normal cells (single plugin cell) and stacked cells (multiple subcells).
   */
  #notifyCellRecycled(
    oldContentType: Option<string>,
    $cell: SheetCell,
  ): void {
    for (const property of ["position", "top", "width"]) $cell.style.removeProperty(property);
    const pluginId = $cell.getAttribute(CELL_BINDING_PLUGIN_ATTR) ?? oldContentType;
    if (!pluginId) return;

    const pluginRole = DefaultBinderHelpers.parseCellBindingPluginRole(
      $cell.getAttribute(CELL_BINDING_PLUGIN_ROLE_ATTR),
    );
    const plugin = pluginRole
      ? this.handle.pluginManager.getPlugin(pluginId, pluginRole)
      : this.handle.pluginManager.getPlugin(pluginId, null);
    const pluginCells = $cell.getPluginCells();
    for (const $pluginCell of pluginCells) {
      plugin.onCellRecycled?.($pluginCell);
    }
  }

  /**
   * Tripwire: a zero-height plan on a VISIBLE, non-transparent cell. The
   * 2026-08-06 header investigation PROVED plans never carry a spurious 0 —
   * the zero writer was `scrubPreviousSheetVisualState` zeroing the DOM out
   * of band while plans said 45 (this breadcrumb stayed silent across the
   * full red repro). So if this ever fires, a NEW zero source exists in plan
   * generation itself. Every plan applier (full-rebind, reflow, resize)
   * reports through here — with the SDK 2.0 switch live, survivor cells take
   * the reflow/resize paths, not the full rebind. `mapH: 0` = the layout map
   * carried the zero; `mapH` non-zero with a zero plan = the plan was built
   * from a stale layout snapshot. (Transparent surface-container rows are
   * height-0 by design but render hidden, so they never reach these sites.)
   */
  #noteZeroHeightPlan(
    path: string,
    plan: RenderPlan<ColumnIndex.Key.Resolved.Default>,
    row: SheetRow.Joined.Handle,
    sheet: SheetViewModel.Surface<SheetBinder>,
  ): void {
    if (plan.layout?.cellHeightPx !== 0) return;
    Tracing.log("cell/zero-height-plan", {
      path,
      rid: row.id.toString(),
      specH: row.layoutSpec(sheet.timescale).cellHeight,
      mapH: sheet.layout.getRowLayout(row.id)?.cellHeightPx ?? -1,
    });
  }

  /**
   * Apply a render plan to a cell (resize path - no content changes).
   */
  #applyPlanResize(
    $cell: SheetCell,
    plan: RenderPlan<ColumnIndex.Key.Resolved.Default>,
    row: SheetRow.Joined.Handle,
    sheet: SheetViewModel.Surface<SheetBinder>,
    column: ColumnIndex.Key.Resolved.Default,
    widthPx: number,
    now: DateTime<Utc>,
    applyContext: ApplyContext,
  ): void {
    if (!this.#applyCurrent(applyContext)) return;
    // Update mode tracking
    this.#updateRowMode(plan.rowId, plan.mode, $cell);
    if (!this.#applyCurrent(applyContext)) return;

    // Apply span mode
    $cell.applySpanMode(plan.spanMode);
    if (!this.#applyCurrent(applyContext)) return;

    // Always bind height
    if (plan.layout) {
      this.#noteZeroHeightPlan("resize", plan, row, sheet);
      $cell.bindHeight(plan.layout.cellHeightPx);
    if (!this.#applyCurrent(applyContext)) return;
    }

    // Skip content update if hidden
    if (plan.skipContent) return;

    // Resize plugin cells
    DefaultBinderHelpers.resizePluginCells($cell, column, plan.stacking?.subColumns ?? null, {
      sheet,
      row,
      widthPx,
      inThePast: plan.timeState.inThePast,
      isCurrent: plan.timeState.isCurrent,
      handle: this.handle,
      now,
    });
    if (!this.#applyCurrent(applyContext)) return;
  }

  /**
   * Apply a render plan to a cell (full rebind path - includes content changes).
   */
  #applyPlanFullRebind(
    $cell: SheetCell,
    plan: RenderPlan<ColumnIndex.Key.Resolved.Default>,
    row: SheetRow.Joined.Handle,
    sheet: SheetViewModel.Surface<SheetBinder>,
    column: ColumnIndex.Key.Resolved.Default,
    widthPx: number,
    now: DateTime<Utc>,
    rowIndex: number,
    binding: Option<CellBinding>,
    applyContext: ApplyContext,
  ): void {
    if (!this.#applyCurrent(applyContext)) return;
    // N3-3: a started rebind invalidates the cell's completed stamp until it completes.
    this.#completedBinds.delete($cell);
    // LR6 render skip, observe-only: only a cell already bound to THIS row and
    // THIS column (semantic date) at the plan's height is skip-eligible; this site
    // computes the decision and pre-bind fingerprint, and the bind always proceeds.
    // Why (column axis, height axis, 2026-08-06 break): lona-dom project docs,
    // binder-history.md#lr6-skip-eligibility.
    const sameColumn = DefaultBinder.cellDisplaysColumn($cell, column);
    const rc = this.#renderCursor;
    let decision: Option<RenderSkip.Decision> = null;
    let preFingerprint: Option<DefaultBinder.CellFingerprint> = null;
    // Height is part of "unchanged output": the cursor's row version tracks the
    // data stream only, but the cell's DOM height can be zeroed OUT-OF-BAND —
    // `scrubPreviousSheetVisualState` binds every rendered cell to 0 on a sheet
    // navigation, and `SheetColumn.onRecycle` zeroes a pooled column's cells —
    // both on the contract that the next bind pass ALWAYS rebinds real heights.
    // A skip breaks that contract: a system row (`system(header)` — same
    // identity on every sheet) surviving a same-config switch matches rid +
    // column + unchanged version, so an honored skip would leave the scrubbed
    // zero pinned for the life of the sheet with no violation marker
    // (2026-08-06 operator break, 8/8 reproducible). Skip eligibility therefore
    // also requires the cell to already display the plan's height.
    if (
      rc != null &&
      sameColumn &&
      cellStructureHonorable($cell, plan, binding) &&
      plan.layout?.cellHeightPx === $cell.height &&
      $cell.getAttribute("rid") === row.id.toString()
    ) {
      // ONE DECIDER: no skip is authorised here; `decideSkip` over the host's
      // eligibility is the only authority. History (sdk-0 C1, the removed
      // `shouldSkip` return): binder-history.md#one-decider.
      decision = this.#decideOnce(rc, row.id);
      if (decision.wouldSkip) preFingerprint = DefaultBinder.fingerprint($cell);
    }

    // `rid` is the cell's record of the row it is bound to — the single
    // authority the onBind reconcile diffs against (sdk-2.0 C2/P5). Write it
    // up-front so it is recorded on EVERY bind path, including the no-layout
    // and `skipContent` early-returns below. Otherwise a span-continuation
    // cell keeps a stale rid; when its row is removed the reconcile cannot
    // match it for deletion, and it lingers as an opacity:0 height-bearing
    // slot — the "phantom gap" zombie.
    $cell.setAttribute("rid", row.id.toString());
    // Placement identity beside the wire one: a promote re-keys the row in
    // place and skips the rebind; the reconcile maps a stale rid back to its
    // row through this (see reconcileRowKeys).
    $cell.setAttribute("rsid", row.id.serial);

    if (!plan.layout) {
      this.#updateRowMode(plan.rowId, plan.mode, $cell);
      if (!this.#step(applyContext, () => $cell.applySpanMode({ type: "none" }))) return;
      if (!this.#step(applyContext, () => $cell.toggleAttribute("hidden", true))) return;
      $cell.style.setProperty("display", "none");
      this.#finishRenderSkip(decision, preFingerprint, $cell, row.id, false,
      applyContext,
    );
      return;
    }

    $cell.toggleAttribute("hidden", false);
    $cell.style.setProperty("display", "block");
    $cell.setId({ row: rowIdentity(row.id.toString()), col: column });

    this.#noteZeroHeightPlan("full-rebind", plan, row, sheet);

    // Update ARIA attributes for screen reader accessibility
    // aria-colindex and aria-rowindex are 1-based
    const colIndex = column.offset + 1;
    const ariaRowIndex = rowIndex + 1;
    const datePartial = column.discrete()[0];
    // Note: cellValue is not available here without loading data, so we pass null
    // The aria-label will be updated when bindCell is called with actual data
    const ariaLabel = row.buildAriaLabel(datePartial, null);
    if (!this.#applyCurrent(applyContext)) return;
    $cell.bindAriaAttributes(colIndex, ariaRowIndex, ariaLabel);
    if (!this.#applyCurrent(applyContext)) return;

    // Update mode tracking and apply span mode
    this.#updateRowMode(plan.rowId, plan.mode, $cell);
    if (!this.#applyCurrent(applyContext)) return;
    $cell.applySpanMode(plan.spanMode);
    if (!this.#applyCurrent(applyContext)) return;

    // Register static child spans
    if (plan.mode.type === "spanned" && plan.mode.isStatic) {
      this.#getStaticChildManager().register(row.id, row, sheet.timescale);
    }
    if (plan.mode.type === "span-stacked") {
      this.#getStaticChildManager().registerSpanStacked(
        row.id,
        row,
        sheet.timescale,
        plan.mode.spanTimescale,
        plan.mode.spanCount,
      );
    }

    // If skipping content, just bind height
    if (plan.skipContent) {
      $cell.bindHeight(plan.layout.cellHeightPx);
      if (!this.#applyCurrent(applyContext)) return;
      this.#finishRenderSkip(decision, preFingerprint, $cell, row.id, false,
      applyContext,
    );
      return;
    }

    const subColumns = plan.stacking?.subColumns ?? null;
    const { inThePast, isCurrent } = plan.timeState;

    const pluginManager = this.handle.pluginManager;
    // `binding` is resolved ONCE per row by the caller and threaded in (onBind
    // hoists it before any structure mutation and feeds the SAME value to the
    // scene build; `rebind` resolves its own). A `null` binding is a mid-rebuild
    // alias (e.g. a calendar alias whose timeline[column] child is transiently
    // un-materialized during a churn-driven re-expansion) that momentarily
    // violates the "row is cell-bindable" contract — degrade to height-only this
    // pass (the `sheet/recycling/mismatch` guard); the next rebind paints content.
    if (!binding) {
      $cell.bindHeight(plan.layout.cellHeightPx);
      if (!this.#applyCurrent(applyContext)) return;
      this.#finishRenderSkip(decision, preFingerprint, $cell, row.id, false,
      applyContext,
    );
      return;
    }
    const contentIsStacked = $cell.$content instanceof CellGroup;
    const expectedIsStacked = plan.stacking != null;
    const contentReusable =
      !!$cell.$content &&
      $cell.contentType === binding.contentType &&
      contentIsStacked === expectedIsStacked;
    if (!contentReusable) {
      // Dispose the OUTGOING content before replacing it: a keep-cell whose
      // content type flipped (e.g. a stacking change on a timescale switch)
      // otherwise drops whole CellGroups of plugin cells — ProseMirror editors
      // included — without `onCellRecycled`, so their editors and listeners
      // stay alive and accumulate across switches. The cell still carries the
      // old content's plugin attrs here (`bindCellPluginAttributes` runs
      // after), which is exactly what `notifyCellRecycled` resolves by.
      if ($cell.$content) {
        this.#notifyCellRecycled($cell.contentType ?? null, $cell);
        if (!this.#applyCurrent(applyContext)) return;
      }
      const $content =
        plan.layout.stackingInfo && subColumns
          ? this.#cellContentBuilder.buildStackedContent(
              row,
              pluginManager,
              subColumns,
              plan.layout,
              now,
              this.onFocusCell,
              this.onBlurCell,
            )
          : binding.plugin.makeCell();
      if (!this.#applyCurrent(applyContext)) return;

      $cell.bindContent(binding.contentType, $content);
      if (!this.#applyCurrent(applyContext)) return;
      DefaultBinderHelpers.bindCellPluginAttributes($cell, binding.binding);
    }

    // Bind layout
    $cell.bindLayout(
      plan.layout.stackingInfo
        ? { ...plan.layout, options: SheetCell.LayoutOptions.EMPTY }
        : plan.layout,
      plan.layout.stackingInfo ? false : inThePast,
    );
    if (!this.#applyCurrent(applyContext)) return;

    // (rid is written up-front for all bind paths; see top of this method.)

    // Bind plugin cells
    DefaultBinderHelpers.bindPluginCells($cell, column, subColumns, {
      sheet,
      row,
      widthPx,
      inThePast,
      isCurrent,
      handle: this.handle,
      now,
    });
    if (!this.#applyCurrent(applyContext)) return;

    // Ensure focus callbacks on recycled stacked cells
    if ($cell.$content instanceof CellGroup) {
      $cell.$content.onFocusCell = this.onFocusCell;
      $cell.$content.onBlurCell = this.onBlurCell;
    }

    // Content for THIS generation is now bound (bindContent and/or bindPluginCells
    // ran). Stamp so Sheet.rebindCellHeights only applies an early height to cells
    // whose content matches the current generation (the stretch guard). Placed at
    // the END so the content-skipping early returns above (null-layout, skipContent,
    // degraded !binding) intentionally leave the stamp stale — those cells get
    // height+content atomically on a later drain and must NOT receive an early
    // height meanwhile.
    $cell.boundGeneration = applyContext.generation;
    this.#completedBinds.set($cell, {
      epoch: applyContext.epoch,
      rowId: row.id.toString(),
      inThePast: plan.timeState.inThePast,
      isCurrent: plan.timeState.isCurrent,
    });

    // LR6: the full rebind completed — referee the would-skip decision against
    // the cell's post-bind output fingerprint, then record the rendered versions
    // for the next bind's compare.
    this.#finishRenderSkip(decision, preFingerprint, $cell, row.id, true,
    applyContext,
  );
  }

  /**
   * Apply a render plan to a survivor cell WITHOUT rebuilding content — the
   * per-cell `resize` / `span` directive path (honored only when
   * the SDK 2.0 switch). Derived line-by-line from `applyPlanFullRebind` as
   * full-rebind MINUS the content rebuild (`bindContent` /
   * `bindCellPluginAttributes` / `bindPluginCells`) and the `boundGeneration`
   * stamp: it keeps `setId` + ARIA colindex, REFRESHES ARIA rowindex from the
   * directive index, `updateRowMode` + `applySpanMode` + static-span registration,
   * the FULL `bindLayout` (not the narrower `applyPlanResize`, which misses
   * `bindLayout` / ARIA), and `resizePluginCells`. Content is unchanged, so
   * `boundGeneration` is NOT advanced and the cursor is marked painted (a survivor
   * keep whose output is still valid) rather than running the would-skip decision.
   */
  #applyPlanReflow(
    $cell: SheetCell,
    plan: RenderPlan<ColumnIndex.Key.Resolved.Default>,
    row: SheetRow.Joined.Handle,
    sheet: SheetViewModel.Surface<SheetBinder>,
    column: ColumnIndex.Key.Resolved.Default,
    widthPx: number,
    now: DateTime<Utc>,
    rowIndex: number,
    binding: Option<CellBinding>,
    reflowHonorable: boolean,
    applyContext: ApplyContext,
  ): void {
    if (!this.#applyCurrent(applyContext)) return;
    // Scene history is keyed by numeric offset, while a pooled DOM cell may
    // still paint another date or timescale at that offset. A resize/span
    // directive can retain content only when this occupant already paints the
    // requested row and semantic column. Check before setId overwrites it.
    if (!reflowHonorable) {
      this.#applyPlanFullRebind(
        $cell, plan, row, sheet, column, widthPx, now, rowIndex, binding,
        applyContext,
      );
      return;
    }
    // rid is a survivor keep's record of its row — already equal to row.id here,
    // but written for parity with the phantom-gap discipline (recorded on EVERY
    // bind path). No LR6 would-skip decision on the reflow path.
    $cell.setAttribute("rid", row.id.toString());
    // Placement identity beside the wire one: a promote re-keys the row in
    // place and skips the rebind; the reconcile maps a stale rid back to its
    // row through this (see reconcileRowKeys).
    $cell.setAttribute("rsid", row.id.serial);

    if (!plan.layout) {
      this.#updateRowMode(plan.rowId, plan.mode, $cell);
      if (!this.#step(applyContext, () => $cell.applySpanMode({ type: "none" }))) return;
      if (!this.#step(applyContext, () => $cell.toggleAttribute("hidden", true))) return;
      $cell.style.setProperty("display", "none");
      // Partial paint — never let this cell skip next time.
      this.#renderCursor?.evict(row.id);
      return;
    }

    $cell.toggleAttribute("hidden", false);
    $cell.style.setProperty("display", "block");
    $cell.setId({ row: rowIdentity(row.id.toString()), col: column });

    this.#noteZeroHeightPlan("reflow", plan, row, sheet);

    // Refresh ARIA: colindex is stable, rowindex tracks the directive index (the
    // row's position may have moved even when its content did not).
    const colIndex = column.offset + 1;
    const ariaRowIndex = rowIndex + 1;
    const datePartial = column.discrete()[0];
    const ariaLabel = row.buildAriaLabel(datePartial, null);
    if (!this.#applyCurrent(applyContext)) return;
    $cell.bindAriaAttributes(colIndex, ariaRowIndex, ariaLabel);
    if (!this.#applyCurrent(applyContext)) return;

    this.#updateRowMode(plan.rowId, plan.mode, $cell);
    if (!this.#applyCurrent(applyContext)) return;
    $cell.applySpanMode(plan.spanMode);
    if (!this.#applyCurrent(applyContext)) return;

    if (plan.mode.type === "spanned" && plan.mode.isStatic) {
      this.#getStaticChildManager().register(row.id, row, sheet.timescale);
    }
    if (plan.mode.type === "span-stacked") {
      this.#getStaticChildManager().registerSpanStacked(
        row.id,
        row,
        sheet.timescale,
        plan.mode.spanTimescale,
        plan.mode.spanCount,
      );
    }

    if (plan.skipContent) {
      $cell.bindHeight(plan.layout.cellHeightPx);
      if (!this.#step(applyContext, () => this.#renderCursor?.evict(row.id))) return;
      return;
    }

    const subColumns = plan.stacking?.subColumns ?? null;
    const { inThePast, isCurrent } = plan.timeState;

    // A transiently un-cell-bindable alias (null binding) can't reflow content —
    // degrade to height-only this pass (mirrors the full-rebind guard).
    if (!binding) {
      $cell.bindHeight(plan.layout.cellHeightPx);
      if (!this.#step(applyContext, () => this.#renderCursor?.evict(row.id))) return;
      return;
    }

    // Full bindLayout (height + offset + past styling) — NOT rebuilding content.
    $cell.bindLayout(
      plan.layout.stackingInfo
        ? { ...plan.layout, options: SheetCell.LayoutOptions.EMPTY }
        : plan.layout,
      plan.layout.stackingInfo ? false : inThePast,
    );
    if (!this.#applyCurrent(applyContext)) return;

    // Re-lay-out existing plugin cells at the new geometry (no content rebuild).
    DefaultBinderHelpers.resizePluginCells($cell, column, subColumns, {
      sheet,
      row,
      widthPx,
      inThePast,
      isCurrent,
      handle: this.handle,
      now,
    });
    if (!this.#applyCurrent(applyContext)) return;

    // Content unchanged: do NOT advance boundGeneration. The survivor's cursor
    // record from its last real paint stays valid — mark it painted.
    applyContext.cursor?.markPainted(row.id);
  }

  /**
   * Rebind a single row when its data changes.
   * Uses the same render plan infrastructure as onBind for consistency.
   */
  rebind(
    sheet: SheetViewModel.Surface<SheetBinder>,
    column: ColumnIndex.Key.Resolved,
    item: {
      $header: SheetColumn;
      $body: SheetColumn;
      $footer: Option<SheetColumn>;
    },
    widthPx: number,
    rowIndex: number,
    currentTime: LiveData.Readable<DateTime<Utc>>,
  ) {
    const resolvedColumn = column.castToDefault();
    if (resolvedColumn == null) {
      Tracing.err("ui")("unknown column-indx");
      return;
    }
    this.#ensureRenderSkip(sheet);
    this.#decisionsThisBind.clear();
    item.$header.assignStyles({
      "--z": "",
    });
    item.$body.assignStyles({
      "--z": "",
    });

    const $cells: SheetCell[] = [
      ...item.$header.$cells(),
      ...item.$body.$cells(),
      ...(item.$footer?.$cells() ?? []),
    ];

    const row = sheet.joinedTree.flattened.rows[rowIndex];
    const $cell = row == null ? null : $cells.find(($cell) => $cell.getAttribute("rid") === row.id.toString() || $cell.getAttribute("rsid") === row.id.serial);
    if (
      row == null ||
      $cell == null ||
      $cell.getAttribute("rid") !== row.id.toString()
    ) {
      // Benign TOCTOU, not an error: synchronous rebindRow indexes into
      // `flattened.rows` here, but re-aligning `$cells` to the flattened
      // order is the burst-coalesced rebindStructure's job — a structure
      // change landing in the window between them mismatches the `rid`.
      // Skipping is the correct move (the trailing rebindStructure pass
      // re-binds this row), so leave a breadcrumb, not an err.
      Tracing.log("rebind-row/rid-mismatch-skip", {
        rowIndex,
        rid: $cell?.getAttribute("rid") ?? null,
        rowId: row?.id.toString() ?? null,
      });
      return;
    }

    const now = currentTime.get();

    // Use render plan infrastructure for consistency with onBind
    const context = this.#createPlanContext(sheet, resolvedColumn, widthPx, now);
    const plans = RenderPlan.makeForRows(
      context,
      [row],
      this.#previousRenderState,
      "full",
      this.#subcolumnCache,
    );

    const plan = plans[0];
    if (!plan) return;

    // Resolve this row's binding once and thread it into the shared full-rebind
    // path (the non-throwing resolver: a transient un-cell-bindable alias yields
    // null → height-only this pass).
    const binding = DefaultBinderHelpers.resolveCellBindingOrSkip(
      row,
      this.handle.pluginManager,
    );

    // A targeted paint can partially mutate native/plugin content before it
    // throws. Retire the old scene and cursor evidence before those effects.
    const applyContext = this.#captureApplyContext();
    const contextEpoch = applyContext.epoch;
    const cursor = applyContext.cursor;
    this.#liveScene.invalidate(resolvedColumn, [row.id]);
    cursor?.evict(row.id);
    try {
      this.#applyPlanFullRebind(
        $cell, plan, row, sheet, resolvedColumn, widthPx, now, rowIndex, binding,
        applyContext,
      );
    } catch (error) {
      cursor?.evict(row.id);
      if (this.#contextEpoch === contextEpoch) {
        this.#previousRenderState.delete(row.id);
        // Keep mode ownership for the next apply's normal span cleanup; it is
        // effect bookkeeping, not evidence that content painted successfully.
        this.#decisionsThisBind.delete(row.id);
      }
      throw error;
    }
    if (this.#contextEpoch === contextEpoch) this.#updatePreviousState(plans);

  }

  onBind(
    sheet: SheetViewModel.Surface<SheetBinder>,
    column: ColumnIndex.Key.Resolved,
    columns: {
      $header: SheetColumn;
      $body: SheetColumn;
      $footer: Option<SheetColumn>;
    },
    widthPx: number,
    pass: SheetBinder.Pass,
    currentTime: LiveData.Readable<DateTime<Utc>>,
  ) {
    // The whole bind, so the binder's share of a frame is not understated by
    // its inner `binder.prepare` / `binder.apply-cells` marks alone.
    const onBindStart = FrameMarks.begin();
    try {
      this.#onBind(sheet, column, columns, widthPx, pass, currentTime);
    } finally {
      FrameMarks.end("binder.on-bind", onBindStart, column.offset, 0, column.offset);
    }
  }

  #onBind(
    sheet: SheetViewModel.Surface<SheetBinder>,
    column: ColumnIndex.Key.Resolved,
    {
      $header,
      $body,
      $footer,
    }: {
      $header: SheetColumn;
      $body: SheetColumn;
      $footer: Option<SheetColumn>;
    },
    widthPx: number,
    pass: SheetBinder.Pass,
    _currentTime: LiveData.Readable<DateTime<Utc>>,
  ) {
    const defaultColumn = column.castToDefault();
    if (defaultColumn == null) {
      Tracing.err("ui")("index-mapper gave us an invalid column index");
      return;
    }
    this.#ensureRenderSkip(sheet);
    this.#decisionsThisBind.clear();

    $header.toggleCellsVisibility(true);
    $body.toggleCellsVisibility(true);
    if ($footer) $footer.toggleCellsVisibility(true);

    const now = this.currentTime.get();
    const $cells: SheetCell[] = [
      ...$header.$cells(),
      ...$body.$cells(),
      ...($footer?.$cells() ?? []),
    ];
    const residency = this.#residencyOf(sheet);
    const { rows } = residency.select(pass.rowViewport);
    const applyContext = this.#captureApplyContext();
    const residentIds = new Set(rows.map((row) => row.id));
    for (const id of this.#previousRenderState.keys()) if (!residentIds.has(id)) {
      this.#previousRenderState.delete(id);
      this.#rowModes.delete(id);
      this.#staticChildManager?.unregister(id);
      this.#renderCursor?.evict(id);
    }
    const targetWires = rows.map((row) => row.id.toString());
    const membershipMatches = $cells.length === rows.length && $cells.every(($cell, slot) =>
      $cell.getAttribute("rid") === targetWires[slot]);
    const windowed = pass.rowViewport.mode === "window";
    const membershipOnly = isMembershipOnlyPass(pass);
    $body.bindRowExtent(windowed ? sheet.layout.bodyHeightPx : null);
    const placeCells = (): void => {
      for (const $cell of $cells) {
        const serial = $cell.getAttribute("rsid");
        const row = serial == null ? residency.rowForWire($cell.getAttribute("rid") ?? "") : residency.rowsBySerial.get(serial);
        const layout = row == null ? null : sheet.layout.getRowLayout(row.id);
        const body = row != null && sheet.joinedTree.getRowLocation(row.id) === "body";
        placeCell(
          $cell.style,
          windowed && body ? "absolute" : "",
          windowed && body && layout != null ? `${layout.offsetPx - sheet.layout.headerHeightPx}px` : "",
          windowed && body ? "100%" : "",
        );
      }
    };
    const input: ColumnPassInput = {
      sheet, defaultColumn, $header, $body, $footer, $cells, rows, residency,
      widthPx, now, applyContext, placeCells,
    };
    if (pass.update === "resize-only" && membershipMatches) {
      ColumnBindPasses.resizeOnly(this.#port, input);
      return;
    }
    ColumnBindPasses.full(this.#port, input, membershipOnly);
  }

  #updatePreviousState(
    plans: RenderPlan<ColumnIndex.Key.Resolved.Default>[],
  ): void {
    for (const plan of plans) {
      this.#previousRenderState.set(plan.rowId, {
        descriptorType: plan.descriptorType,
        isStacked: plan.stacking != null,
        cellCount: plan.stacking?.subColumns.length ?? 0,
        isNew: false,
      });
    }
  }
}

export namespace DefaultBinder {
  /** A cell's last completed full rebind (N3-3); see `cell-identity.ts`. */
  export type CompletedBind = $CompletedBind;
  /** A cell's output fingerprint (LR6 referee); see `cell-identity.ts`. */
  export type CellFingerprint = $CellFingerprint;
  /** A pending per-cell referee sample; see `cell-identity.ts`. */
  export type PendingCellSample = $PendingCellSample;
  export const cellDisplaysColumn = $cellDisplaysColumn;
  export const cellDisplaysRowAndColumn = $cellDisplaysRowAndColumn;
  export const fingerprint = $fingerprint;
  export const fingerprintChanged = $fingerprintChanged;
}

/** Whether `pass` says it is membership-only (`SheetBinder.ReasonedPass`); a pass without a reason is a content pass. */
function isMembershipOnlyPass(pass: SheetBinder.Pass): boolean {
  return "reason" in pass && pass.reason === "membership" && pass.update === "full";
}
